import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useSeo } from "@/lib/seo";

const inputCls = "w-full border border-tertiary bg-white px-4 py-3 text-sm outline-none transition-colors duration-300 focus:border-champagne focus:ring-1 focus:ring-champagne";

export default function MemberLogin() {
  const { user, login } = useAuth();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needVerify, setNeedVerify] = useState(false);
  const [loading, setLoading] = useState(false);
  useSeo({ title: t("member_login"), lang, robots: "noindex,nofollow" });

  if (user) return <Navigate to={user.role === "member" ? "/account" : "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNeedVerify(false);
    try {
      const u = await login(email, password);
      navigate(u.role === "member" ? "/account" : "/admin");
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) setNeedVerify(true);
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email }); toast.success(t("signup_resend")); } catch { /* ignore */ }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-16">
      <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI Connect</span>
      <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite">{t("login_title")}</h1>
      <form onSubmit={submit} data-testid="member-login-form" className="mt-8 space-y-4 border border-tertiary bg-white p-8">
        <div>
          <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{t("form_email")}</label>
          <input required type="email" data-testid="member-login-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-anthracite/50">{lang === "fr" ? "Mot de passe" : "Password"}</label>
          <input required type="password" data-testid="member-login-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        </div>
        {error && <p data-testid="member-login-error" className="text-sm text-destructive">{error}</p>}
        {needVerify && (
          <button type="button" onClick={resend} className="text-sm text-champagne underline">{t("signup_resend")}</button>
        )}
        <button type="submit" disabled={loading} data-testid="member-login-submit" className="w-full bg-anthracite py-3.5 text-sm font-medium text-ivory transition-colors hover:bg-black disabled:opacity-50">
          {loading ? "…" : t("member_login")}
        </button>
        <p className="text-center text-sm text-anthracite/50">
          {lang === "fr" ? "Pas encore de compte ?" : "No account yet?"} <Link to="/signup" className="text-champagne">{t("member_signup")}</Link>
        </p>
      </form>
    </div>
  );
}
